/**
 * Blog Post 21: 5 Common Mortgage Mistakes First-Time Buyers Make
 * 首次買樓5大常見按揭錯誤
 */
import { useEffect } from 'react';
import { useCustomTranslation } from '../hooks';
import { Box, Container, Typography } from '@mui/material';
import { trackPageView } from '../utils/analytics';
import SEO from '../components/common/SEO';

export default function BlogPost21() {
    const { i18n } = useCustomTranslation();
    const isEnglish = i18n.language === 'en';

    useEffect(() => {
        trackPageView('/blog/5-mortgage-mistakes-first-time-buyers-hong-kong', 'Blog Post 21');
        document.title = isEnglish
            ? '5 Common Mortgage Mistakes First-Time Buyers Make in Hong Kong'
            : '首次買樓5大常見按揭錯誤 | PayWise365';
    }, [isEnglish]);

    const seoTitle = isEnglish
        ? '5 Common Mortgage Mistakes First-Time Buyers Make in Hong Kong'
        : '首次買樓5大常見按揭錯誤';

    const seoDescription = isEnglish
        ? 'Avoid these 5 costly mortgage mistakes when buying your first home in Hong Kong. Learn about stress tests, fixed rates, bank switching, and hidden fees.'
        : '避開首次買樓時5個常見按揭錯誤。了解壓力測試、定息按揭、轉按及隱藏收費。';

    const seoKeywords = isEnglish
        ? 'mortgage mistakes Hong Kong, first-time buyer errors, 首次買樓, home buying mistakes, mortgage tips HK, first home Hong Kong 2026'
        : '首次買樓, 按揭錯誤, 買樓陷阱, 香港按揭, 首置錯誤, 置業貼士 2026';

    return (
        <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
            <SEO
                title={seoTitle}
                description={seoDescription}
                keywords={seoKeywords}
                canonicalUrl="https://www.paywise365.com/blog/5-mortgage-mistakes-first-time-buyers-hong-kong"
                ogType="article"
                ogImage="https://www.paywise365.com/logo.svg"
                publishDate="2026-05-18T00:00:00+08:00"
                modifiedDate="2026-05-18T00:00:00+08:00"
                author="PayWise365"
                articleSection={isEnglish ? 'First-Time Buyer' : '首次置業'}
                tags={isEnglish
                    ? ['Mortgage Mistakes', 'First-Time Buyer', 'Home Buying', 'Hong Kong Property', 'Mortgage Tips']
                    : ['按揭錯誤', '首次置業', '買樓', '香港物業', '按揭貼士']
                }
            />
            <Box
                sx={{
                    background: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
                    color: 'white',
                    py: { xs: 6, md: 8 },
                }}
            >
                <Container maxWidth="lg">
                    <Box sx={{ mb: 3, fontSize: '14px', opacity: 0.9 }}>
                        <a href="/" style={{ color: 'white', textDecoration: 'none' }}>
                            {isEnglish ? 'Home' : '首頁'}
                        </a>
                        {' > '}
                        <a href="/blog" style={{ color: 'white', textDecoration: 'none' }}>
                            {isEnglish ? 'Blog' : '資訊'}
                        </a>
                        {' > '}
                        <span style={{ opacity: 0.8 }}>
                            {isEnglish ? 'Mortgage Mistakes' : '按揭錯誤'}
                        </span>
                    </Box>

                    <Typography variant="h2" sx={{ mb: 2, fontWeight: 700 }}>
                        {isEnglish
                            ? '5 Common Mortgage Mistakes First-Time Buyers Make'
                            : '首次買樓5大常見按揭錯誤'}
                    </Typography>
                    <Typography variant="h6" sx={{ opacity: 0.95, fontWeight: 400 }}>
                        {isEnglish
                            ? 'Learn from others\' mistakes and save thousands in Hong Kong'
                            : '從他人錯誤中學習，在香港置業時節省數萬元'}
                    </Typography>
                </Container>
            </Box>

            <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
                <Box sx={{ color: '#475569', lineHeight: 1.8, fontSize: '1rem' }}>
                    {isEnglish ? (
                        <>
                            <Typography sx={{ mb: 3 }}>
                                Buying your first home in Hong Kong is exciting—but a single mortgage misstep can cost you tens of thousands. Here are the 5 most common mistakes (and how to avoid them).
                            </Typography>

                            <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, color: '#1a1a2e', mt: 4 }}>
                                1. Ignoring the Stress Test
                            </Typography>
                            <Typography sx={{ mb: 2 }}>
                                <strong>The mistake:</strong> Not checking if you can pass the stress test (mortgage rate + 3%) before falling in love with a property.
                            </Typography>
                            <Typography sx={{ mb: 3 }}>
                                <strong>The fix:</strong> Use our <a href="/calculators/loan-affordability" style={{ color: '#667eea' }}>loan affordability calculator</a> first. If your monthly repayment exceeds 50% of income at stressed rates, you'll fail—even if the bank pre-approves you.
                            </Typography>

                            <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, color: '#1a1a2e', mt: 4 }}>
                                2. Choosing Fixed Rate Without Understanding the Lock-In
                            </Typography>
                            <Typography sx={{ mb: 2 }}>
                                <strong>The mistake:</strong> Locking into a 2-year fixed rate at 3.5% without realizing floating rates (H+1.3% ≈ 4.5%) might drop if HIBOR falls.
                            </Typography>
                            <Typography sx={{ mb: 3 }}>
                                <strong>The fix:</strong> Fixed rates offer certainty but limit flexibility. Compare total cost over the lock-in period vs. floating rate scenarios before committing.
                            </Typography>

                            <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, color: '#1a1a2e', mt: 4 }}>
                                3. Not Shopping Around for Better Rates
                            </Typography>
                            <Typography sx={{ mb: 2 }}>
                                <strong>The mistake:</strong> Accepting the first bank's offer (e.g., P-2.5%) without negotiating or comparing 10+ banks.
                            </Typography>
                            <Typography sx={{ mb: 3 }}>
                                <strong>The fix:</strong> Even 0.1% difference = HK$20,000+ saved on a HK$5M loan. Use a mortgage broker or spend 2 hours comparing online. Don't leave money on the table.
                            </Typography>

                            <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, color: '#1a1a2e', mt: 4 }}>
                                4. Underestimating Hidden Costs
                            </Typography>
                            <Typography sx={{ mb: 2 }}>
                                <strong>The mistake:</strong> Budgeting only for down payment + stamp duty, then getting blindsided by legal fees (HK$15K), survey fees (HK$5K), renovation, and moving costs.
                            </Typography>
                            <Typography sx={{ mb: 3 }}>
                                <strong>The fix:</strong> Add 3-5% of property value as a buffer for all closing costs and immediate expenses. Keep 6 months of living expenses separate as an emergency fund.
                            </Typography>

                            <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, color: '#1a1a2e', mt: 4 }}>
                                5. Forgetting About Mortgage Insurance (If LTV &gt; 60%)
                            </Typography>
                            <Typography sx={{ mb: 2 }}>
                                <strong>The mistake:</strong> Assuming you can borrow 90% without factoring in HKMC insurance premiums (up to 5% of loan amount).
                            </Typography>
                            <Typography sx={{ mb: 3 }}>
                                <strong>The fix:</strong> High LTV mortgages require insurance. On a HK$4M loan at 90% LTV, insurance could cost HK$150K–200K. Add this to your budget upfront.
                            </Typography>

                            <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, color: '#1a1a2e', mt: 4 }}>
                                Quick Checklist Before You Sign
                            </Typography>
                            <ul>
                                <li style={{ marginBottom: '8px' }}>✅ Passed stress test at +3% interest rate</li>
                                <li style={{ marginBottom: '8px' }}>✅ Compared at least 5 banks' mortgage offers</li>
                                <li style={{ marginBottom: '8px' }}>✅ Budgeted for all hidden costs (legal, survey, insurance)</li>
                                <li style={{ marginBottom: '8px' }}>✅ Understand fixed vs floating rate tradeoffs</li>
                                <li style={{ marginBottom: '8px' }}>✅ Have 6-month emergency fund after closing</li>
                            </ul>

                            <Typography sx={{ mt: 4, pt: 3, borderTop: '1px solid #e2e8f0', fontSize: '0.9rem', color: '#64748b' }}>
                                <strong>Published:</strong> 18 May 2026 | <strong>Read time:</strong> 3 minutes
                            </Typography>
                        </>
                    ) : (
                        <>
                            <Typography sx={{ mb: 3 }}>
                                在香港首次買樓令人興奮——但一個按揭錯誤可能令你損失數萬元。以下是5個最常見錯誤（及如何避免）。
                            </Typography>

                            <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, color: '#1a1a2e', mt: 4 }}>
                                1. 忽視壓力測試
                            </Typography>
                            <Typography sx={{ mb: 2 }}>
                                <strong>錯誤：</strong>在愛上某物業前，沒有檢查能否通過壓力測試（按揭利率 + 3%）。
                            </Typography>
                            <Typography sx={{ mb: 3 }}>
                                <strong>解決方法：</strong>先使用我們的<a href="/calculators/loan-affordability" style={{ color: '#667eea' }}>借貸能力計算機</a>。若在壓力利率下每月還款超過收入50%，即使銀行預先批核，你也會不合格。
                            </Typography>

                            <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, color: '#1a1a2e', mt: 4 }}>
                                2. 選擇定息按揭但不理解鎖定期
                            </Typography>
                            <Typography sx={{ mb: 2 }}>
                                <strong>錯誤：</strong>鎖定2年定息3.5%，卻不知道若HIBOR下跌，浮息按揭（H+1.3% ≈ 4.5%）可能更平。
                            </Typography>
                            <Typography sx={{ mb: 3 }}>
                                <strong>解決方法：</strong>定息提供確定性但限制靈活性。在承諾前比較鎖定期內的總成本與浮息情境。
                            </Typography>

                            <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, color: '#1a1a2e', mt: 4 }}>
                                3. 不貨比三家尋找更佳利率
                            </Typography>
                            <Typography sx={{ mb: 2 }}>
                                <strong>錯誤：</strong>接受第一間銀行報價（如P-2.5%），沒有議價或比較10多間銀行。
                            </Typography>
                            <Typography sx={{ mb: 3 }}>
                                <strong>解決方法：</strong>即使0.1%差異在HK$500萬貸款上可省HK$2萬+。使用按揭經紀或花2小時網上比較。別讓錢白白流失。
                            </Typography>

                            <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, color: '#1a1a2e', mt: 4 }}>
                                4. 低估隱藏成本
                            </Typography>
                            <Typography sx={{ mb: 2 }}>
                                <strong>錯誤：</strong>只預算首期+印花稅，然後被律師費（HK$15K）、驗樓費（HK$5K）、裝修及搬遷費用所震驚。
                            </Typography>
                            <Typography sx={{ mb: 3 }}>
                                <strong>解決方法：</strong>加3-5%物業價值作為所有交易成本及即時開支的緩衝。另外保留6個月生活費作為應急基金。
                            </Typography>

                            <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, color: '#1a1a2e', mt: 4 }}>
                                5. 忘記按揭保險（若按揭成數 &gt; 60%）
                            </Typography>
                            <Typography sx={{ mb: 2 }}>
                                <strong>錯誤：</strong>以為可借90%而沒計算按保保費（最高達貸款額5%）。
                            </Typography>
                            <Typography sx={{ mb: 3 }}>
                                <strong>解決方法：</strong>高成數按揭需要保險。在HK$400萬貸款90%按揭成數下，保費可達HK$15-20萬。預先將此加入預算。
                            </Typography>

                            <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, color: '#1a1a2e', mt: 4 }}>
                                簽約前快速檢查清單
                            </Typography>
                            <ul>
                                <li style={{ marginBottom: '8px' }}>✅ 通過+3%利率壓力測試</li>
                                <li style={{ marginBottom: '8px' }}>✅ 比較至少5間銀行按揭報價</li>
                                <li style={{ marginBottom: '8px' }}>✅ 預算所有隱藏成本（律師、驗樓、保險）</li>
                                <li style={{ marginBottom: '8px' }}>✅ 了解定息與浮息利弊</li>
                                <li style={{ marginBottom: '8px' }}>✅ 成交後有6個月應急基金</li>
                            </ul>

                            <Typography sx={{ mt: 4, pt: 3, borderTop: '1px solid #e2e8f0', fontSize: '0.9rem', color: '#64748b' }}>
                                <strong>發佈日期：</strong>2026年5月18日 | <strong>閱讀時間：</strong>3分鐘
                            </Typography>
                        </>
                    )}
                </Box>
            </Container>
        </Box>
    );
}
